export interface KeyState {
    forward: boolean;
    backward: boolean;
    left: boolean;
    right: boolean;
    up: boolean;
    down: boolean;
    cardSlots: boolean[];
    cancel: boolean;
}

export class KeyboardBindings {
    private state: KeyState = {
        forward: false,
        backward: false,
        left: false,
        right: false,
        up: false,
        down: false,
        cardSlots: [false, false, false, false, false, false],
        cancel: false,
    };
    private actionCb: ((action: string, slot?: number) => void) | null = null;
    private kdHandler: (e: KeyboardEvent) => void;
    private kuHandler: (e: KeyboardEvent) => void;
    private blurHandler: () => void;
    private attached = false;

    constructor() {
        this.kdHandler = (e) => {
            // Ignore key input when the user is typing into a text field
            // (username, future chat box, etc.). Otherwise typing "1234"
            // would dispatch four card-activate commands.
            const t = e.target as HTMLElement | null;
            if (
                t &&
                (t.tagName === 'INPUT' ||
                    t.tagName === 'TEXTAREA' ||
                    t.isContentEditable)
            ) {
                return;
            }
            // Ctrl/Cmd-modified shortcuts run BEFORE the regular per-key
            // switch so e.g. Ctrl+D doesn't double-fire as movement-right.
            // Only the InputManager knows whether admin mode is active —
            // we just emit the action; gating happens upstream.
            if (e.ctrlKey || e.metaKey) {
                if (e.code === 'KeyD') {
                    this.actionCb?.('admin_spawn_dummy');
                    e.preventDefault?.();
                    return;
                }
                if (e.code === 'KeyK') {
                    this.actionCb?.('admin_damage_self');
                    e.preventDefault?.();
                    return;
                }
                if (e.code === 'KeyR') {
                    this.actionCb?.('admin_reset_arena');
                    e.preventDefault?.();
                    return;
                }
            }
            switch (e.code) {
                case 'KeyW': case 'ArrowUp':
                    this.state.forward = true; break;
                case 'KeyS': case 'ArrowDown':
                    this.state.backward = true; break;
                case 'KeyA': case 'ArrowLeft':
                    this.state.left = true; break;
                case 'KeyD': case 'ArrowRight':
                    this.state.right = true; break;
                case 'Space':
                    this.state.up = true; e.preventDefault(); break;
                case 'ShiftLeft': case 'ShiftRight':
                    this.state.down = true; break;
                case 'Escape':
                    this.actionCb?.('cancel'); break;
                case 'KeyQ':
                    this.actionCb?.('trigger_ability'); break;
                case 'KeyE':
                    this.actionCb?.('fire_basic'); break;
                case 'KeyU':
                    this.actionCb?.('trigger_ability_slot', 0); break;
                case 'KeyI':
                    this.actionCb?.('trigger_ability_slot', 1); break;
                case 'KeyO':
                    this.actionCb?.('trigger_ability_slot', 2); break;
                case 'KeyJ':
                    this.actionCb?.('trigger_ability_slot', 3); break;
                case 'KeyK':
                    this.actionCb?.('trigger_ability_slot', 4); break;
                case 'KeyL':
                    this.actionCb?.('trigger_ability_slot', 5); break;
                case 'Digit1': case 'Digit2': case 'Digit3':
                case 'Digit4': case 'Digit5': case 'Digit6': {
                    const slot = parseInt(e.code.charAt(5)) - 1;
                    this.state.cardSlots[slot] = true;
                    this.actionCb?.('activate_card', slot);
                    break;
                }
            }
        };
        this.kuHandler = (e) => {
            switch (e.code) {
                case 'KeyW': case 'ArrowUp':
                    this.state.forward = false; break;
                case 'KeyS': case 'ArrowDown':
                    this.state.backward = false; break;
                case 'KeyA': case 'ArrowLeft':
                    this.state.left = false; break;
                case 'KeyD': case 'ArrowRight':
                    this.state.right = false; break;
                case 'Space':
                    this.state.up = false; break;
                case 'ShiftLeft': case 'ShiftRight':
                    this.state.down = false; break;
                case 'Digit1': case 'Digit2': case 'Digit3':
                case 'Digit4': case 'Digit5': case 'Digit6':
                    this.state.cardSlots[parseInt(e.code.charAt(5)) - 1] = false;
                    break;
            }
        };
        // Window blur drops keyup events (alt-tab, devtools focus, etc.).
        // Without this reset, a held W key stays "forward = true" and the
        // drone keeps moving when the user returns — violating Invariant #1.
        this.blurHandler = () => this.zeroAllKeyFlags();
    }

    private zeroAllKeyFlags(): void {
        this.state.forward = false;
        this.state.backward = false;
        this.state.left = false;
        this.state.right = false;
        this.state.up = false;
        this.state.down = false;
        this.state.cancel = false;
        this.state.cardSlots.fill(false);
    }

    getState(): KeyState { return this.state; }

    onKeyAction(cb: (action: string, slot?: number) => void): void {
        this.actionCb = cb;
    }

    attach(): void {
        if (!this.attached) {
            window.addEventListener('keydown', this.kdHandler);
            window.addEventListener('keyup', this.kuHandler);
            window.addEventListener('blur', this.blurHandler);
            this.attached = true;
        }
    }

    detach(): void {
        if (this.attached) {
            window.removeEventListener('keydown', this.kdHandler);
            window.removeEventListener('keyup', this.kuHandler);
            window.removeEventListener('blur', this.blurHandler);
            this.attached = false;
        }
    }
}
